// iPhone transport: react-native-webrtc straight to OpenAI Realtime (GA), a 1:1
// port of DirectRealtimeWeb. POST SDP offer -> /v1/realtime/calls with the
// ephemeral client secret; events flow over the "oai-events" data channel.
//
// react-native-webrtc is a native module that only exists in builds made after it
// was added. It is therefore required lazily inside connect() so older binaries
// (which receive this JS over the air) fail with a clear message instead of
// crashing at import time.
import type { MediaStream as RNMediaStream, RTCPeerConnection as RNPeerConnection } from 'react-native-webrtc';
import type { LiveEvents, VoiceTransport } from './VoiceTransport';

type RTCDataChannel = ReturnType<RNPeerConnection['createDataChannel']>;
const CALLS_URL = 'https://api.openai.com/v1/realtime/calls';
const NEEDS_UPDATE = 'Update OLMPS from TestFlight to use live voice';

type WebRTCModule = typeof import('react-native-webrtc');
type InCall = {
  start(setup?: { media?: 'audio' | 'video'; auto?: boolean; ringback?: string }): void;
  stop(setup?: { busytone?: string }): void;
  setForceSpeakerphoneOn(flag: boolean): void;
};

function loadWebRTC(): WebRTCModule | null {
  try {
    const m = require('react-native-webrtc') as WebRTCModule;
    return m?.RTCPeerConnection && m?.mediaDevices ? m : null;
  } catch {
    return null;
  }
}

function loadInCall(): InCall | null {
  try {
    const m = require('react-native-incall-manager');
    const mgr = (m?.default ?? m) as InCall | undefined;
    return mgr && typeof mgr.start === 'function' ? mgr : null;
  } catch {
    return null;
  }
}

export class DirectRealtimeNative implements VoiceTransport {
  private pc: RNPeerConnection | null = null;
  private dc: RTCDataChannel | null = null;
  private stream: RNMediaStream | null = null;
  private ev: LiveEvents | null = null;
  private abort: AbortController | null = null;
  private inCall: InCall | null = null;
  private gen = 0;
  private muted = false;
  private sawBufferEvents = false;

  async connect(creds: { clientSecret: string; model: string }, ev: LiveEvents): Promise<void> {
    this.teardown();
    const gen = ++this.gen;
    const stale = () => gen !== this.gen;
    this.ev = ev;
    ev.onState('connecting');

    const rtc = loadWebRTC();
    if (!rtc) {
      ev.onState('error', NEEDS_UPDATE);
      return;
    }
    const abort = new AbortController();
    this.abort = abort;
    try {
      // Loudspeaker routing: WebRTC's voice-chat audio session defaults to the
      // earpiece on iPhone; InCallManager (media 'video') forces the speaker.
      const inCall = loadInCall();
      if (inCall) {
        inCall.start({ media: 'video', auto: false });
        inCall.setForceSpeakerphoneOn(true);
        this.inCall = inCall;
      }

      const pc = new rtc.RTCPeerConnection({ iceServers: [] });
      this.pc = pc;

      const stream = (await rtc.mediaDevices.getUserMedia({ audio: true })) as RNMediaStream;
      if (stale()) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      this.stream = stream;
      const track = stream.getAudioTracks()[0];
      track.enabled = !this.muted;
      pc.addTrack(track, stream);

      const dc = pc.createDataChannel('oai-events');
      this.dc = dc;
      dc.onmessage = (m: { data: unknown }) => {
        if (stale()) return;
        try {
          this.handle(JSON.parse(String(m.data)));
        } catch {
          // ignore malformed event
        }
      };
      dc.onopen = () => {
        // Semantic turn detection, low eagerness: waits out mid-sentence pauses instead of cutting the user off
        // after a fixed silence, but still answers promptly once they've clearly finished.
        this.send({ type: 'session.update', session: { type: 'realtime', audio: { input: { turn_detection: { type: 'semantic_vad', eagerness: 'low' } } } } });
        if (!stale()) ev.onState('connected');
      };
      pc.onconnectionstatechange = () => {
        if (stale()) return;
        const s = pc.connectionState;
        if (s === 'disconnected') ev.onState('reconnecting');
        else if (s === 'connected' && dc.readyState === 'open') ev.onState('connected');
        else if (s === 'failed') ev.onState('error', 'Voice connection lost');
      };

      const offer = await pc.createOffer({});
      await pc.setLocalDescription(offer);
      if (stale()) return;

      const res = await fetch(CALLS_URL, {
        method: 'POST',
        body: offer.sdp,
        headers: { Authorization: `Bearer ${creds.clientSecret}`, 'Content-Type': 'application/sdp' },
        signal: abort.signal,
      });
      if (stale()) return;
      if (!res.ok) throw new Error(`Realtime connect failed (${res.status})`);
      const sdp = await res.text();
      if (stale()) return;
      await pc.setRemoteDescription({ type: 'answer', sdp } as never);
    } catch (e) {
      if (stale() || (e instanceof Error && e.name === 'AbortError')) return;
      const msg = e instanceof Error ? e.message : 'Could not start live voice';
      this.teardown();
      ev.onState('error', msg);
    }
  }

  private handle(e: { type?: string; [k: string]: unknown }): void {
    const ev = this.ev;
    if (!ev) return;
    switch (e.type) {
      case 'response.output_audio_transcript.delta':
      case 'response.audio_transcript.delta':
        ev.onTranscript('deity', String(e.delta ?? ''), false);
        break;
      case 'response.output_audio_transcript.done':
      case 'response.audio_transcript.done':
        ev.onTranscript('deity', String(e.transcript ?? ''), true);
        break;
      case 'conversation.item.input_audio_transcription.completed': {
        const text = String(e.transcript ?? '').trim();
        if (text) ev.onTranscript('user', text, true);
        break;
      }
      case 'input_audio_buffer.speech_started':
        ev.onBargeIn?.();
        ev.onUserSpeaking?.(true);
        break;
      case 'input_audio_buffer.speech_stopped':
        ev.onUserSpeaking?.(false);
        break;
      case 'output_audio_buffer.started':
        this.sawBufferEvents = true;
        ev.onSpeaking(true);
        break;
      case 'output_audio_buffer.stopped':
      case 'output_audio_buffer.cleared':
        this.sawBufferEvents = true;
        ev.onSpeaking(false);
        break;
      case 'response.created':
        if (!this.sawBufferEvents) ev.onSpeaking(true);
        break;
      case 'response.done':
        if (!this.sawBufferEvents) ev.onSpeaking(false);
        break;
      case 'response.function_call_arguments.done': {
        let args: Record<string, unknown> = {};
        try {
          args = JSON.parse(String(e.arguments ?? '{}'));
        } catch {
          // leave empty
        }
        ev.onToolCall(String(e.call_id ?? ''), String(e.name ?? ''), args);
        break;
      }
      case 'error': {
        const err = e.error as { message?: string } | undefined;
        ev.onState('error', err?.message || 'Live voice error');
        break;
      }
    }
  }

  private send(obj: unknown): void {
    if (this.dc?.readyState === 'open') this.dc.send(JSON.stringify(obj));
  }

  setMuted(m: boolean): void {
    this.muted = m;
    this.stream?.getAudioTracks().forEach((t) => (t.enabled = !m));
  }

  // Web-only (avatar lips); native plays the remote audio itself.
  getRemoteAudioTrack(): MediaStreamTrack | null {
    return null;
  }

  setDirectPlayback(_muted: boolean): void {}

  interrupt(): void {
    this.send({ type: 'response.cancel' });
    this.send({ type: 'output_audio_buffer.clear' });
  }

  sendContext(text: string, respond = false): void {
    this.send({ type: 'conversation.item.create', item: { type: 'message', role: 'system', content: [{ type: 'input_text', text }] } });
    if (respond) this.send({ type: 'response.create' });
  }

  sendToolResult(callId: string, output: string): void {
    this.send({ type: 'conversation.item.create', item: { type: 'function_call_output', call_id: callId, output } });
    this.send({ type: 'response.create' });
  }

  private teardown(): void {
    this.abort?.abort();
    this.abort = null;
    try {
      this.dc?.close();
    } catch {
      // already closed
    }
    this.dc = null;
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    try {
      this.pc?.close();
    } catch {
      // already closed
    }
    this.pc = null;
    try {
      this.inCall?.stop();
    } catch {
      // ignore
    }
    this.inCall = null;
    this.sawBufferEvents = false;
  }

  disconnect(): void {
    this.gen++;
    const ev = this.ev;
    this.ev = null;
    this.teardown();
    ev?.onState('ended');
  }
}
